import type { ReactElement, ReactNode } from "react";

import { useDispatch, useSelector } from "react-redux";

import { Tooltip } from "@similarweb/ui-components";

import { AiFilterModeButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterModeButton/AiFilterModeButton";
import { FilterModeButton } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterModeButton/FilterModeButton";
import { useDemoModeDisabled } from "app/pages/sales-intelligence/hooks/demo-mode/useDemoModeDisabled";
import type { FilterModeType } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/misc";
import { setSyncFilterManualChangeMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/lead-generator/store";

import advancedSearchFilteringTrackingService from "../../services/tracking";
import { setFilterMode } from "../../store/action-creators";
import { selectFilterMode } from "../../store/selectors";

import { StyledDemoDisabledTab, StyledTab, StyledTabList, StyledTabPanel, StyledTabs } from "./styles";

type TabbedFilterPanelContainerProps = {
    listSlot: ReactNode;
    chatSlot: ReactNode;
    withTabSwitch?: boolean;
};

export const TabbedFilterPanelContainer = ({
    listSlot,
    chatSlot,
    withTabSwitch = true,
}: TabbedFilterPanelContainerProps): ReactElement => {
    const dispatch = useDispatch();

    const filterMode = useSelector(selectFilterMode);
    // AI prospecting is unavailable in the read-only demo preview; the toggle stays visible but disabled.
    const { isDisabled: isAiModeDisabled, disabledTooltip } = useDemoModeDisabled();

    const tabIds: readonly FilterModeType[] = ["filters-list", "ai-agent"];

    const selectedTabIndex = tabIds.findIndex((id) => id === filterMode);

    const handleAiAgentButtonClick = () => void dispatch(setSyncFilterManualChangeMessage());

    const handleFilterModeChange = (mode: FilterModeType): void => {
        advancedSearchFilteringTrackingService.filtersPanel.trackChangeFilteringMode(mode);

        dispatch(setFilterMode(mode));
    };

    const handleTabSelect = (index: number): void => {
        const selectedMode = tabIds[index];

        if (!selectedMode || (isAiModeDisabled && selectedMode === "ai-agent")) {
            return;
        }

        handleFilterModeChange(selectedMode);
    };

    return (
        <StyledTabs
            selectedIndex={selectedTabIndex}
            forceRenderTabPanel
            onSelect={handleTabSelect}
        >
            {withTabSwitch ? (
                <StyledTabList>
                    <StyledTab data-automation="filters-list-tab">
                        <FilterModeButton isSelected={filterMode === "filters-list"} />
                    </StyledTab>

                    <StyledTab data-automation="ai-agent-tab">
                        <Tooltip
                            content={disabledTooltip}
                            enabled={isAiModeDisabled}
                            placement="top"
                        >
                            <StyledDemoDisabledTab $isDisabled={isAiModeDisabled}>
                                <AiFilterModeButton
                                    isSelected={filterMode === "ai-agent"}
                                    onClick={isAiModeDisabled ? undefined : handleAiAgentButtonClick}
                                />
                            </StyledDemoDisabledTab>
                        </Tooltip>
                    </StyledTab>
                </StyledTabList>
            ) : null}

            <StyledTabPanel data-automation="filters-list-tab-panel">{listSlot}</StyledTabPanel>

            <StyledTabPanel data-automation="ai-agent-tab-panel">{chatSlot}</StyledTabPanel>
        </StyledTabs>
    );
};
